import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Grid,
  Typography,
  TextField,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Card,
  CardContent,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  InputAdornment,
  Alert,
  Snackbar
} from '@mui/material';
import {
  Add as AddIcon,
  Remove as RemoveIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  QrCodeScanner as BarcodeIcon,
  ShoppingCart as CartIcon,
  Payment as PaymentIcon,
  Receipt as ReceiptIcon,
  Store as StoreIcon,
  FileDownload as DownloadIcon
} from '@mui/icons-material';
import * as XLSX from 'xlsx';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { API_BASE_URL } from '../apiConfig';

const POSSystem = () => {
  const [stores, setStores] = useState([]);
  const [selectedStore, setSelectedStore] = useState(null);
  const [cart, setCart] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [barcodeInput, setBarcodeInput] = useState('');
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [cashAmount, setCashAmount] = useState('');
  const [cardAmount, setCardAmount] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [discount, setDiscount] = useState(0);
  const [discountType, setDiscountType] = useState('fixed');
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [salesHistoryOpen, setSalesHistoryOpen] = useState(false);
  const [salesHistory, setSalesHistory] = useState([]);
  const [loadingSales, setLoadingSales] = useState(false);
  
  const barcodeInputRef = useRef(null);

  // RTL theme for Arabic with iOS styling
  const theme = createTheme({
    direction: 'rtl',
    typography: {
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Cairo", "Roboto", sans-serif'
    },
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            fontSize: '1rem',
            padding: '10px 20px',
            borderRadius: '14px',
            textTransform: 'none',
            fontWeight: 600,
            boxShadow: 'none',
            '&:hover': {
              boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
            }
          }
        }
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiInputBase-input': {
              fontSize: '1rem'
            },
            '& .MuiOutlinedInput-root': {
              borderRadius: '12px',
              backgroundColor: 'rgba(0,0,0,0.02)'
            }
          }
        }
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            borderRadius: '16px'
          }
        }
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: '16px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
          }
        }
      }
    }
  });

  useEffect(() => {
    loadStores();
  }, []);

  const loadSalesHistory = async () => {
    if (!selectedStore) {
      setError('الرجاء اختيار متجر');
      return;
    }

    try {
      setLoadingSales(true);
      const response = await fetch(
        `${API_BASE_URL}/api/erp_pos.php?action=sales_history&store_id=${selectedStore.id}`
      );
      const data = await response.json();
      
      if (data.success) {
        setSalesHistory(data.sales || []);
        setSalesHistoryOpen(true);
      } else {
        setError('فشل في تحميل سجل المبيعات');
      }
    } catch (error) {
      console.error('Error loading sales history:', error);
      setError('حدث خطأ في تحميل سجل المبيعات');
    } finally {
      setLoadingSales(false);
    }
  };

  const exportSalesHistoryToExcel = () => {
    try {
      if (!salesHistory || salesHistory.length === 0) {
        setError('لا توجد مبيعات للتصدير');
        return;
      }

      // Flatten sales items for export
      const rows = [];
      salesHistory.forEach(sale => {
        if (sale.items && Array.isArray(sale.items)) {
          sale.items.forEach(item => {
            rows.push({
              'رقم البيع': sale.sale_number || sale.id,
              'التاريخ': new Date(sale.created_at).toLocaleString('ar-IQ'),
              'اسم المنتج': item.product_name || '',
              'SKU': item.product_sku || '',
              'الكمية': item.quantity || 0,
              'سعر الشراء': item.purchase_price !== '' && item.purchase_price != null ? Number(item.purchase_price) : '',
              'سعر البيع': item.unit_price != null ? Number(item.unit_price) : '',
              'الإجمالي': item.subtotal != null ? Number(item.subtotal) : '',
              'العميل': sale.customer_name || 'غير محدد',
              'طريقة الدفع': sale.payment_method === 'cash' ? 'نقدي' : sale.payment_method === 'card' ? 'بطاقة' : 'مختلط'
            });
          });
        }
      });

      if (rows.length === 0) {
        setError('لا توجد منتجات للتصدير');
        return;
      }

      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(rows);
      
      // Set column widths
      ws['!cols'] = [
        { wch: 12 }, // رقم البيع
        { wch: 20 }, // التاريخ
        { wch: 30 }, // اسم المنتج
        { wch: 15 }, // SKU
        { wch: 8 },  // الكمية
        { wch: 12 }, // سعر الشراء
        { wch: 12 }, // سعر البيع
        { wch: 12 }, // الإجمالي
        { wch: 20 }, // العميل
        { wch: 12 }  // طريقة الدفع
      ];
      
      XLSX.utils.book_append_sheet(wb, ws, 'Sales History');
      const dateStr = new Date().toISOString().slice(0, 10);
      XLSX.writeFile(wb, `sales_history_${dateStr}.xlsx`);
      
      setSuccess('تم تصدير سجل المبيعات بنجاح');
    } catch (e) {
      console.error('Excel export error:', e);
      setError('فشل تصدير ملف الإكسل');
    }
  };

  useEffect(() => {
    if (barcodeInputRef.current) {
      barcodeInputRef.current.focus();
    }
  }, []);

  const loadStores = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/erp_stores.php?action=list`);
      const data = await response.json();
      if (data.success) {
        const activeStores = data.stores.filter(s => s.is_active === '1' || s.is_active === 1);
        setStores(activeStores);
        if (activeStores.length > 0) {
          setSelectedStore(activeStores[0]);
        }
      }
    } catch (error) {
      console.error('Error loading stores:', error);
      setError('فشل في تحميل المتاجر');
    }
  };

  const handleBarcodeKeyPress = async (e) => {
    if (e.key === 'Enter' && barcodeInput.trim()) {
      await searchByBarcode(barcodeInput.trim());
      setBarcodeInput('');
    }
  };

  const searchByBarcode = async (barcode) => {
    if (!selectedStore) {
      setError('الرجاء اختيار متجر أولاً');
      return;
    }

    try {
      setLoading(true);
      const response = await fetch(
        `${API_BASE_URL}/api/erp_pos.php?action=product_by_barcode&barcode=${barcode}&store_id=${selectedStore.id}`
      );
      const data = await response.json();
      
      if (data.success && data.product) {
        addToCart(data.product);
      } else {
        setError('المنتج غير موجود');
      }
    } catch (error) {
      console.error('Error searching by barcode:', error);
      setError('فشل في البحث عن المنتج');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = useCallback(async (query) => {
    if (!selectedStore || query.length < 2) {
      setSearchResults([]);
      return;
    }

    try {
      const response = await fetch(
        `${API_BASE_URL}/api/erp_pos.php?action=search_product&query=${encodeURIComponent(query)}&store_id=${selectedStore.id}`
      );
      const data = await response.json();
      
      if (data.success) {
        setSearchResults(data.products || []);
      }
    } catch (error) {
      console.error('Error searching products:', error);
    }
  }, [selectedStore]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchQuery) {
        handleSearch(searchQuery);
      } else {
        setSearchResults([]);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchQuery, handleSearch]);

  const addToCart = (product) => {
    const existingItem = cart.find(item => item.product_id === product.id);
    
    if (existingItem) {
      setCart(cart.map(item =>
        item.product_id === product.id
          ? { ...item, quantity: item.quantity + 1, subtotal: (item.quantity + 1) * item.unit_price }
          : item
      ));
    } else {
      setCart([...cart, {
        product_id: product.id,
        product_name: product.name,
        product_sku: product.sku,
        barcode: product.barcode,
        brand: product.brand || '',
        quantity: 1,
        unit_price: parseFloat(product.sell_price || product.price || 0),
        purchase_price: (product.purchase_price != null && product.purchase_price !== '')
          ? parseFloat(product.purchase_price)
          : (product.price != null && product.price !== '' ? parseFloat(product.price) : ''),
        stock_quantity: parseFloat(product.stock_quantity || 0),
        discount_amount: 0,
        subtotal: parseFloat(product.sell_price || product.price || 0)
      }]);
    }
    
    setSearchQuery('');
    setSearchResults([]);
    
    if (barcodeInputRef.current) {
      barcodeInputRef.current.focus();
    }
  };

  // Export current cart to Excel (name, sku, brand, purchase & sell price)
  const exportCartToExcel = () => {
    try {
      const rows = (cart || []).map((item) => ({
        Name: item.product_name || '',
        SKU: item.product_sku || '',
        Brand: item.brand || '',
        PurchasePrice: item.purchase_price !== '' && item.purchase_price != null ? Number(item.purchase_price) : '',
        SellPrice: item.unit_price != null ? Number(item.unit_price) : ''
      }));

      if (!rows.length) {
        setError('السلة فارغة، لا توجد بيانات للتصدير');
        return;
      }

      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(rows);
      XLSX.utils.book_append_sheet(wb, ws, 'Sale Items');
      const dateStr = new Date().toISOString().slice(0, 10);
      XLSX.writeFile(wb, `sale_items_${dateStr}.xlsx`);
    } catch (e) {
      console.error('Excel export error:', e);
      setError('فشل تصدير ملف الإكسل');
    }
  };

  const updateQuantity = (productId, delta) => {
    setCart(cart.map(item => {
      if (item.product_id === productId) {
        const newQty = Math.max(1, item.quantity + delta);
        return {
          ...item,
          quantity: newQty,
          subtotal: newQty * item.unit_price - item.discount_amount
        };
      }
      return item;
    }));
  };

  const removeFromCart = (productId) => {
    setCart(cart.filter(item => item.product_id !== productId));
  };

  const clearCart = () => {
    if (window.confirm('هل أنت متأكد من مسح السلة؟')) {
      setCart([]);
      setDiscount(0);
      setCustomerName('');
      setCustomerPhone('');
    }
  };

  const calculateTotals = () => {
    const subtotal = cart.reduce((sum, item) => sum + item.subtotal, 0);
    let discountAmount = 0;
    
    if (discountType === 'percentage') {
      discountAmount = (subtotal * discount) / 100;
    } else {
      discountAmount = discount;
    }
    
    const total = subtotal - discountAmount;
    
    return { subtotal, discountAmount, total };
  };

  const handleOpenPayment = () => {
    if (cart.length === 0) {
      setError('السلة فارغة');
      return;
    }
    
    const { total } = calculateTotals();
    setCashAmount(total.toString());
    setCardAmount('');
    setPaymentDialogOpen(true);
  };

  const handleCompleteSale = async () => {
    if (!selectedStore) {
      setError('الرجاء اختيار متجر');
      return;
    }

    const { subtotal, discountAmount, total } = calculateTotals();
    
    let cash = parseFloat(cashAmount) || 0;
    let card = parseFloat(cardAmount) || 0;
    let totalPaid = cash + card;
    
    if (totalPaid < total) {
      setError(`المبلغ المدفوع أقل من الإجمالي. المطلوب: ${total.toFixed(0)} IQD`);
      return;
    }

    const change = totalPaid - total;

    try {
      setLoading(true);
      
      const saleData = {
        action: 'create_sale',
        store_id: selectedStore.id,
        customer_name: customerName || null,
        customer_phone: customerPhone || null,
        items: cart,
        subtotal: subtotal,
        discount_amount: discountAmount,
        discount_type: discountType,
        tax_amount: 0,
        total_amount: total,
        payment_method: paymentMethod,
        cash_amount: cash,
        card_amount: card,
        change_amount: change
      };

      const response = await fetch(`${API_BASE_URL}/api/erp_pos.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(saleData)
      });

      const data = await response.json();
      
      if (data.success) {
        setSuccess(`تم البيع بنجاح! رقم البيع: ${data.sale_number}`);
        
        setCart([]);
        setDiscount(0);
        setCustomerName('');
        setCustomerPhone('');
        setCashAmount('');
        setCardAmount('');
        setPaymentDialogOpen(false);
        
      } else {
        setError(data.message || 'فشل في إتمام البيع');
      }
    } catch (error) {
      console.error('Error completing sale:', error);
      setError('حدث خطأ في إتمام البيع');
    } finally {
      setLoading(false);
    }
  };

  // Export current search results to Excel
  const exportToExcel = () => {
    try {
      const rows = (searchResults || []).map((p) => ({
        Name: p?.name || '',
        SKU: p?.sku || '',
        Brand: p?.brand || '',
        PurchasePrice: p?.price != null ? Number(p.price) : '',
        SellPrice: p?.sell_price != null ? Number(p.sell_price) : ''
      }));

      if (!rows.length) {
        setError('لا توجد بيانات للتصدير');
        return;
      }

      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(rows);
      XLSX.utils.book_append_sheet(wb, ws, 'Products');
      const dateStr = new Date().toISOString().slice(0, 10);
      XLSX.writeFile(wb, `products_export_${dateStr}.xlsx`);
    } catch (e) {
      console.error('Excel export error:', e);
      setError('فشل تصدير ملف الإكسل');
    }
  };

  const { subtotal, discountAmount, total } = calculateTotals();
  const change = paymentMethod === 'split' 
    ? (parseFloat(cashAmount) || 0) + (parseFloat(cardAmount) || 0) - total
    : (parseFloat(cashAmount) || 0) - total;

  return (
    <ThemeProvider theme={theme}>
      <Box sx={{ minHeight: '100vh', bgcolor: '#f7f7f7', display: 'flex', flexDirection: 'column' }} dir="rtl">
        {/* Top Header Bar - iOS Style */}
        <Box sx={{ 
          bgcolor: 'rgba(255, 255, 255, 0.8)', 
          backdropFilter: 'blur(20px)',
          borderBottom: '0.5px solid rgba(0,0,0,0.08)',
          p: 1
        }}>
          <Grid container spacing={1} alignItems="center">
            <Grid item xs={3}>
              <Button 
                variant="outlined" 
                onClick={clearCart}
                disabled={cart.length === 0}
                size="small"
                sx={{ 
                  borderRadius: '10px',
                  borderColor: 'rgba(0,0,0,0.1)',
                  color: '#007AFF',
                  fontWeight: 600,
                  fontSize: '0.85rem',
                  py: 0.5,
                  '&:hover': {
                    borderColor: '#007AFF',
                    bgcolor: 'rgba(0, 122, 255, 0.05)'
                  }
                }}
              >
                المصاريف
              </Button>
            </Grid>
            <Grid item xs={6}>
              <Box sx={{ display: 'flex', gap: 0.75, justifyContent: 'center' }}>
                <IconButton size="small" sx={{ 
                  bgcolor: 'rgba(0,0,0,0.04)', 
                  borderRadius: '8px',
                  p: 0.5,
                  '&:hover': { bgcolor: 'rgba(0,0,0,0.08)' },
                  transition: 'all 0.2s'
                }}>
                  <StoreIcon sx={{ color: '#007AFF', fontSize: '1.1rem' }} />
                </IconButton>
                <IconButton size="small" sx={{ 
                  bgcolor: 'rgba(0,0,0,0.04)', 
                  borderRadius: '8px',
                  p: 0.5,
                  '&:hover': { bgcolor: 'rgba(0,0,0,0.08)' },
                  transition: 'all 0.2s'
                }}>
                  <PaymentIcon sx={{ color: '#007AFF', fontSize: '1.1rem' }} />
                </IconButton>
                <IconButton 
                  size="small" 
                  onClick={loadSalesHistory}
                  disabled={!selectedStore || loadingSales}
                  sx={{ 
                    bgcolor: 'rgba(0,0,0,0.04)', 
                    borderRadius: '8px',
                    p: 0.5,
                    '&:hover': { bgcolor: 'rgba(0,0,0,0.08)' },
                    transition: 'all 0.2s'
                  }}
                >
                  <ReceiptIcon sx={{ color: '#007AFF', fontSize: '1.1rem' }} />
                </IconButton>
                <IconButton size="small" sx={{ 
                  bgcolor: 'rgba(0,0,0,0.04)', 
                  borderRadius: '8px',
                  p: 0.5,
                  '&:hover': { bgcolor: 'rgba(0,0,0,0.08)' },
                  transition: 'all 0.2s'
                }}>
                  <SearchIcon sx={{ color: '#007AFF', fontSize: '1.1rem' }} />
                </IconButton>
                <IconButton 
                  size="small" 
                  onClick={exportToExcel}
                  sx={{ 
                    bgcolor: 'rgba(0,0,0,0.04)', 
                    borderRadius: '8px',
                    p: 0.5,
                    '&:hover': { bgcolor: 'rgba(0,0,0,0.08)' },
                    transition: 'all 0.2s'
                  }}
                >
                  <DownloadIcon sx={{ color: '#007AFF', fontSize: '1.1rem' }} />
                </IconButton>
              </Box>
            </Grid>
            <Grid item xs={3} sx={{ textAlign: 'left' }}>
              <Chip 
                size="small"
                icon={<StoreIcon sx={{ color: 'white !important', fontSize: '0.9rem' }} />} 
                label={new Date().toLocaleString('ar-IQ', { hour: '2-digit', minute: '2-digit', day: '2-digit', month: '2-digit', year: 'numeric' })}
                sx={{ 
                  bgcolor: '#007AFF', 
                  color: 'white', 
                  fontWeight: 600, 
                  px: 1.5,
                  fontSize: '0.75rem',
                  borderRadius: '8px',
                  boxShadow: '0 2px 8px rgba(0, 122, 255, 0.3)'
                }}
              />
            </Grid>
          </Grid>
        </Box>

        {/* Main Content Area */}
        <Box sx={{ display: 'flex', flexGrow: 1, height: 'calc(100vh - 56px)', gap: 1.5, p: 1.5 }}>
          {/* Left Side - Products Grid */}
          <Box sx={{ 
            width: '60%', 
            display: 'flex', 
            flexDirection: 'column', 
            bgcolor: 'white',
            borderRadius: '20px',
            overflow: 'hidden',
            boxShadow: '0 2px 16px rgba(0,0,0,0.06)'
          }}>
            {/* Tabs */}
            <Box sx={{ display: 'flex', gap: 0.5, p: 1, bgcolor: 'rgba(0,0,0,0.02)' }}>
              <Button 
                variant="contained" 
                size="small"
                sx={{ 
                  flex: 1, 
                  bgcolor: '#007AFF', 
                  '&:hover': { bgcolor: '#0051D5' },
                  borderRadius: '10px',
                  py: 0.75,
                  boxShadow: '0 2px 8px rgba(0, 122, 255, 0.3)',
                  fontWeight: 600
                }}
              >
                <Typography sx={{ fontSize: '0.85rem' }}>المعاملات التجارية</Typography>
              </Button>
              <Button 
                variant="text" 
                size="small"
                sx={{ 
                  flex: 1,
                  color: '#007AFF',
                  borderRadius: '10px',
                  py: 0.75,
                  fontWeight: 600,
                  '&:hover': {
                    bgcolor: 'rgba(0, 122, 255, 0.08)'
                  }
                }}
              >
                <Typography sx={{ fontSize: '0.85rem' }}>الصنف</Typography>
              </Button>
            </Box>

            {/* Search Bar */}
            <Box sx={{ p: 1.5, pt: 1 }}>
              <TextField
                fullWidth
                inputRef={barcodeInputRef}
                value={searchQuery || barcodeInput}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setBarcodeInput(e.target.value);
                }}
                onKeyPress={handleBarcodeKeyPress}
                placeholder="بحث عن منتج..."
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: '#8E8E93' }} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton size="small">
                        <BarcodeIcon sx={{ color: '#007AFF' }} />
                      </IconButton>
                    </InputAdornment>
                  )
                }}
                sx={{ 
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '14px',
                    bgcolor: 'rgba(0,0,0,0.03)',
                    border: 'none',
                    '& fieldset': {
                      border: 'none'
                    },
                    '&:hover': {
                      bgcolor: 'rgba(0,0,0,0.05)'
                    },
                    '&.Mui-focused': {
                      bgcolor: 'white',
                      boxShadow: '0 0 0 3px rgba(0, 122, 255, 0.1)'
                    }
                  }
                }}
              />
            </Box>

            {/* Products Grid */}
            <Box sx={{ flexGrow: 1, overflow: 'auto', p: 1.5, pt: 0 }}>
              <Grid container spacing={1.5}>
                {(searchResults.length > 0 ? searchResults : []).map(product => (
                  <Grid item xs={6} sm={4} md={3} key={product.id}>
                    <Card 
                      sx={{ 
                        cursor: 'pointer',
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        '&:hover': {
                          transform: 'scale(1.02)',
                          boxShadow: '0 8px 20px rgba(0,0,0,0.12)'
                        },
                        '&:active': {
                          transform: 'scale(0.98)'
                        },
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        borderRadius: '16px',
                        overflow: 'hidden',
                        border: '1px solid rgba(0,0,0,0.06)'
                      }}
                      onClick={() => addToCart(product)}
                    >
                      <Box sx={{ 
                        height: 110, 
                        bgcolor: 'rgba(0,0,0,0.02)', 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center',
                        overflow: 'hidden'
                      }}>
                        {product.image ? (
                          <img 
                            src={`${API_BASE_URL}/api/uploads/products/${product.image}`} 
                            alt={product.name}
                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                            onError={(e) => { e.target.style.display = 'none'; }}
                          />
                        ) : (
                          <Box sx={{ fontSize: '3.5rem', opacity: 0.3 }}>📦</Box>
                        )}
                      </Box>
                      <CardContent sx={{ flexGrow: 1, p: 1, '&:last-child': { pb: 1 } }}>
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            fontWeight: 600, 
                            mb: 0.25,
                            fontSize: '0.8rem',
                            minHeight: '2.5em',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            color: '#1C1C1E'
                          }}
                        >
                          {product.name}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#8E8E93', display: 'block', mb: 0.25, fontSize: '0.7rem' }}>
                          {product.sku}
                        </Typography>
                        <Typography variant="body2" sx={{ fontSize: '0.7rem', color: '#34C759', fontWeight: 600 }}>
                          {product.stock_quantity || 0} في المخزون
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
                {searchResults.length === 0 && (
                  <Grid item xs={12}>
                    <Box sx={{ textAlign: 'center', py: 6 }}>
                      <SearchIcon sx={{ fontSize: '3rem', color: '#C7C7CC', mb: 1.5 }} />
                      <Typography sx={{ color: '#8E8E93', fontSize: '0.95rem', fontWeight: 500 }}>
                        {searchQuery.length >= 2 ? 'لا توجد نتائج' : 'ابحث عن منتج لعرضه هنا'}
                      </Typography>
                    </Box>
                  </Grid>
                )}
              </Grid>
            </Box>
          </Box>

          {/* Right Side - Transaction Details */}
          <Box sx={{ 
            width: '40%', 
            display: 'flex', 
            flexDirection: 'column',
            bgcolor: 'white',
            borderRadius: '20px',
            overflow: 'hidden',
            boxShadow: '0 2px 16px rgba(0,0,0,0.06)'
          }}>
            {/* Customer & User Info */}
            <Box sx={{ p: 1.5, bgcolor: 'rgba(0,0,0,0.02)' }}>
              <FormControl fullWidth sx={{ mb: 1.5 }}>
                <InputLabel sx={{ fontSize: '0.85rem' }}>المتجر</InputLabel>
                <Select
                  value={selectedStore?.id || ''}
                  onChange={(e) => {
                    const store = stores.find(s => s.id === e.target.value);
                    setSelectedStore(store);
                    setCart([]);
                  }}
                  size="small"
                  sx={{
                    borderRadius: '12px',
                    bgcolor: 'white',
                    '& fieldset': {
                      border: 'none'
                    },
                    boxShadow: '0 1px 3px rgba(0,0,0,0.06)'
                  }}
                >
                  {stores.map(store => (
                    <MenuItem key={store.id} value={store.id}>
                      {store.store_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              
              <TextField
                fullWidth
                placeholder="اسم العميل (اختياري)"
                value={customerName}
                onChange={(e) => setCustomerName(e.target.value)}
                size="small"
                sx={{ 
                  mb: 1.5,
                  '& .MuiOutlinedInput-root': {
                    bgcolor: 'white',
                    '& fieldset': { border: 'none' },
                    boxShadow: '0 1px 3px rgba(0,0,0,0.06)'
                  }
                }}
              />
              
              <TextField
                fullWidth
                placeholder="رقم الهاتف (اختياري)"
                value={customerPhone}
                onChange={(e) => setCustomerPhone(e.target.value)}
                size="small"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    bgcolor: 'white',
                    '& fieldset': { border: 'none' },
                    boxShadow: '0 1px 3px rgba(0,0,0,0.06)'
                  }
                }}
              />
            </Box>

            {/* Cart */}
            <Box sx={{ flexGrow: 1, overflow: 'auto', bgcolor: 'white' }}>
              {cart.length === 0 ? (
                <Box sx={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  alignItems: 'center', 
                  justifyContent: 'center', 
                  height: '100%',
                  py: 4
                }}>
                  <CartIcon sx={{ fontSize: '3.5rem', color: '#C7C7CC', mb: 1.5 }} />
                  <Typography sx={{ color: '#8E8E93', fontSize: '0.95rem', fontWeight: 500 }}>
                    السلة فارغة
                  </Typography>
                  <Typography sx={{ color: '#C7C7CC', fontSize: '0.8rem', mt: 0.75 }}>
                    أضف منتجات لبدء البيع
                  </Typography>
                </Box>
              ) : (
                <Box sx={{ p: 1.5 }}>
                  {cart.map((item) => (
                    <Box 
                      key={item.product_id}
                      sx={{ 
                        mb: 1.5, 
                        p: 1.5, 
                        bgcolor: 'rgba(0,0,0,0.02)',
                        borderRadius: '14px',
                        border: '1px solid rgba(0,0,0,0.06)',
                        transition: 'all 0.2s',
                        '&:hover': {
                          bgcolor: 'rgba(0,0,0,0.04)'
                        }
                      }}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: '#1C1C1E', mb: 0.25, fontSize: '0.85rem' }}>
                            {item.product_name}
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#8E8E93', fontSize: '0.75rem' }}>
                            {item.unit_price.toFixed(0)} IQD × {item.quantity}
                          </Typography>
                        </Box>
                        <IconButton 
                          size="small" 
                          onClick={() => removeFromCart(item.product_id)}
                          sx={{ 
                            color: '#FF3B30',
                            bgcolor: 'rgba(255, 59, 48, 0.1)',
                            p: 0.5,
                            '&:hover': {
                              bgcolor: 'rgba(255, 59, 48, 0.2)'
                            }
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                      
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: 1,
                          bgcolor: 'white',
                          borderRadius: '8px',
                          p: 0.25,
                          border: '1px solid rgba(0,0,0,0.08)'
                        }}>
                          <IconButton 
                            size="small" 
                            onClick={() => updateQuantity(item.product_id, -1)}
                            sx={{ 
                              bgcolor: 'rgba(0,0,0,0.04)',
                              p: 0.5,
                              '&:hover': { bgcolor: 'rgba(0,0,0,0.08)' }
                            }}
                          >
                            <RemoveIcon sx={{ fontSize: '0.9rem' }} />
                          </IconButton>
                          <Typography sx={{ minWidth: '25px', textAlign: 'center', fontWeight: 600, fontSize: '0.9rem' }}>
                            {item.quantity}
                          </Typography>
                          <IconButton 
                            size="small" 
                            onClick={() => updateQuantity(item.product_id, 1)}
                            sx={{ 
                              bgcolor: '#007AFF',
                              color: 'white',
                              p: 0.5,
                              '&:hover': { bgcolor: '#0051D5' }
                            }}
                          >
                            <AddIcon sx={{ fontSize: '0.9rem' }} />
                          </IconButton>
                        </Box>
                        
                        <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: '#1C1C1E' }}>
                          {item.subtotal.toFixed(0)}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </Box>
              )}
            </Box>

            {/* Bottom Summary */}
            <Box sx={{ p: 1.5, bgcolor: 'rgba(0,0,0,0.02)', borderTop: '1px solid rgba(0,0,0,0.06)' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
                <Typography sx={{ color: '#8E8E93', fontSize: '0.8rem' }}>
                  {cart.length} منتجات
                </Typography>
                <Typography sx={{ color: '#8E8E93', fontSize: '0.8rem' }}>
                  {cart.reduce((sum, item) => sum + item.quantity, 0)} عناصر
                </Typography>
              </Box>
              
              <Box sx={{ 
                bgcolor: 'white', 
                p: 2, 
                borderRadius: '14px', 
                mb: 1.5,
                border: '2px solid #007AFF',
                boxShadow: '0 4px 16px rgba(0, 122, 255, 0.15)'
              }}>
                <Typography sx={{ color: '#8E8E93', fontSize: '0.75rem', mb: 0.25, textAlign: 'center' }}>
                  المجموع الكلي
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 700, textAlign: 'center', color: '#007AFF' }}>
                  {total.toFixed(0)}
                  <Typography component="span" sx={{ fontSize: '0.9rem', fontWeight: 600, ml: 1 }}>
                    IQD
                  </Typography>
                </Typography>
              </Box>

              <Grid container spacing={1}>
                <Grid item xs={6}>
                  <Button
                    fullWidth
                    variant="outlined"
                    size="small"
                    sx={{ 
                      borderColor: '#FF3B30',
                      color: '#FF3B30',
                      py: 1,
                      borderRadius: '14px',
                      fontWeight: 600,
                      '&:hover': { 
                        bgcolor: 'rgba(255, 59, 48, 0.08)',
                        borderColor: '#FF3B30'
                      }
                    }}
                    onClick={clearCart}
                    disabled={cart.length === 0}
                  >
                    إلغاء
                  </Button>
                </Grid>
                <Grid item xs={6}>
                  <Button
                    fullWidth
                    variant="contained"
                    size="small"
                    sx={{ 
                      bgcolor: '#34C759', 
                      py: 1,
                      borderRadius: '14px',
                      fontWeight: 600,
                      boxShadow: '0 4px 12px rgba(52, 199, 89, 0.3)',
                      '&:hover': { 
                        bgcolor: '#28A745',
                        boxShadow: '0 6px 16px rgba(52, 199, 89, 0.4)'
                      }
                    }}
                    onClick={handleOpenPayment}
                    disabled={cart.length === 0 || !selectedStore}
                  >
                    دفع الآن
                  </Button>
                </Grid>
              </Grid>
            </Box>
          </Box>
        </Box>

        {/* Payment Dialog - iOS Style */}
        <Dialog 
          open={paymentDialogOpen} 
          onClose={() => setPaymentDialogOpen(false)} 
          maxWidth="sm" 
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: '20px',
              boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
            }
          }}
        >
          <DialogTitle sx={{ fontSize: '1.3rem', fontWeight: 600, textAlign: 'center', pt: 3, pb: 2 }}>
            إتمام الدفع
          </DialogTitle>
          <DialogContent sx={{ px: 3 }}>
            <Box sx={{ 
              bgcolor: 'linear-gradient(135deg, #007AFF 0%, #5856D6 100%)', 
              background: 'linear-gradient(135deg, #007AFF 0%, #5856D6 100%)',
              p: 3, 
              borderRadius: '18px', 
              mb: 3, 
              textAlign: 'center',
              boxShadow: '0 8px 24px rgba(0, 122, 255, 0.3)'
            }}>
              <Typography sx={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.85rem', mb: 0.5 }}>
                المبلغ المستحق
              </Typography>
              <Typography variant="h3" sx={{ fontWeight: 700, color: 'white' }}>
                {total.toFixed(0)}
              </Typography>
              <Typography sx={{ color: 'rgba(255,255,255,0.9)', fontSize: '0.9rem', mt: 0.5 }}>
                دينار عراقي
              </Typography>
            </Box>

            <Grid container spacing={2.5}>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel sx={{ fontSize: '0.9rem' }}>طريقة الدفع</InputLabel>
                  <Select
                    value={paymentMethod}
                    onChange={(e) => {
                      setPaymentMethod(e.target.value);
                      if (e.target.value === 'cash') {
                        setCashAmount(total.toString());
                        setCardAmount('');
                      } else if (e.target.value === 'card') {
                        setCardAmount(total.toString());
                        setCashAmount('');
                      }
                    }}
                    sx={{
                      borderRadius: '14px',
                      bgcolor: 'rgba(0,0,0,0.02)',
                      '& fieldset': { border: '1px solid rgba(0,0,0,0.1)' }
                    }}
                  >
                    <MenuItem value="cash">💵 نقدي</MenuItem>
                    <MenuItem value="card">💳 بطاقة</MenuItem>
                    <MenuItem value="split">🔄 مختلط</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {(paymentMethod === 'cash' || paymentMethod === 'split') && (
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    type="number"
                    label="المبلغ النقدي"
                    value={cashAmount}
                    onChange={(e) => setCashAmount(e.target.value)}
                    InputProps={{
                      endAdornment: <InputAdornment position="end">IQD</InputAdornment>
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '14px',
                        bgcolor: 'rgba(0,0,0,0.02)',
                        '& fieldset': { border: '1px solid rgba(0,0,0,0.1)' }
                      }
                    }}
                  />
                </Grid>
              )}

              {(paymentMethod === 'card' || paymentMethod === 'split') && (
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    type="number"
                    label="مبلغ البطاقة"
                    value={cardAmount}
                    onChange={(e) => setCardAmount(e.target.value)}
                    InputProps={{
                      endAdornment: <InputAdornment position="end">IQD</InputAdornment>
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '14px',
                        bgcolor: 'rgba(0,0,0,0.02)',
                        '& fieldset': { border: '1px solid rgba(0,0,0,0.1)' }
                      }
                    }}
                  />
                </Grid>
              )}

              {paymentMethod === 'cash' && change >= 0 && cashAmount && (
                <Grid item xs={12}>
                  <Box sx={{ 
                    bgcolor: 'rgba(52, 199, 89, 0.1)', 
                    p: 2.5, 
                    borderRadius: '14px',
                    border: '1px solid rgba(52, 199, 89, 0.3)',
                    textAlign: 'center'
                  }}>
                    <Typography sx={{ fontSize: '0.85rem', color: '#34C759', mb: 0.5 }}>
                      الباقي
                    </Typography>
                    <Typography variant="h5" sx={{ fontWeight: 700, color: '#34C759' }}>
                      {change.toFixed(0)} IQD
                    </Typography>
                  </Box>
                </Grid>
              )}
            </Grid>
          </DialogContent>
            <DialogActions sx={{ p: 3, gap: 1.5 }}>
              <Button 
                onClick={() => setPaymentDialogOpen(false)} 
                size="large"
                sx={{
                  flex: 1,
                  py: 1.25,
                  borderRadius: '14px',
                  color: '#FF3B30',
                  fontWeight: 600,
                  fontSize: '1rem'
                }}
              >
                إلغاء
              </Button>
              <Button
                onClick={exportCartToExcel}
                variant="outlined"
                size="large"
                disabled={cart.length === 0}
                sx={{
                  flex: 1,
                  py: 1.25,
                  borderRadius: '14px',
                  borderColor: '#007AFF',
                  color: '#007AFF',
                  fontWeight: 600,
                  fontSize: '1rem',
                  '&:hover': {
                    bgcolor: 'rgba(0, 122, 255, 0.06)',
                    borderColor: '#007AFF'
                  }
                }}
                startIcon={<DownloadIcon />}
              >
                تصدير إكسل
              </Button>
              <Button 
                onClick={handleCompleteSale} 
                variant="contained" 
                size="large"
                disabled={loading}
                sx={{ 
                  flex: 1.5,
                  bgcolor: '#34C759',
                  py: 1.25,
                  borderRadius: '14px',
                  fontWeight: 600,
                  fontSize: '1rem',
                  boxShadow: '0 4px 16px rgba(52, 199, 89, 0.4)',
                  '&:hover': { 
                    bgcolor: '#28A745',
                    boxShadow: '0 6px 20px rgba(52, 199, 89, 0.5)'
                  }
                }}
              >
                إتمام البيع
              </Button>
            </DialogActions>
        </Dialog>

        {/* Sales History Dialog */}
        <Dialog 
          open={salesHistoryOpen} 
          onClose={() => setSalesHistoryOpen(false)} 
          maxWidth="lg" 
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: '20px',
              boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
              maxHeight: '90vh'
            }
          }}
        >
          <DialogTitle sx={{ fontSize: '1.3rem', fontWeight: 600, textAlign: 'center', pt: 3, pb: 2 }}>
            سجل المبيعات
          </DialogTitle>
          <DialogContent sx={{ px: 3 }}>
            {loadingSales ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <Typography>جاري التحميل...</Typography>
              </Box>
            ) : salesHistory.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 6 }}>
                <ReceiptIcon sx={{ fontSize: '3rem', color: '#C7C7CC', mb: 1.5 }} />
                <Typography sx={{ color: '#8E8E93', fontSize: '0.95rem', fontWeight: 500 }}>
                  لا توجد مبيعات
                </Typography>
              </Box>
            ) : (
              <Box sx={{ overflowY: 'auto', maxHeight: '60vh' }}>
                {salesHistory.map((sale, index) => (
                  <Box 
                    key={sale.id || index}
                    sx={{ 
                      mb: 2, 
                      p: 2, 
                      bgcolor: 'rgba(0,0,0,0.02)',
                      borderRadius: '14px',
                      border: '1px solid rgba(0,0,0,0.06)'
                    }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
                      <Box>
                        <Typography sx={{ fontWeight: 600, color: '#007AFF', mb: 0.5 }}>
                          #{sale.sale_number || sale.id}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#8E8E93' }}>
                          {new Date(sale.created_at).toLocaleString('ar-IQ')}
                        </Typography>
                      </Box>
                      <Box sx={{ textAlign: 'left' }}>
                        <Typography sx={{ fontWeight: 700, fontSize: '1.1rem', color: '#34C759' }}>
                          {parseFloat(sale.total_amount || 0).toFixed(0)} IQD
                        </Typography>
                        <Chip 
                          size="small"
                          label={
                            sale.payment_method === 'cash' ? 'نقدي' : 
                            sale.payment_method === 'card' ? 'بطاقة' : 'مختلط'
                          }
                          sx={{ 
                            bgcolor: '#007AFF', 
                            color: 'white',
                            fontSize: '0.7rem',
                            height: '20px'
                          }}
                        />
                      </Box>
                    </Box>
                    
                    {sale.customer_name && (
                      <Typography variant="caption" sx={{ display: 'block', color: '#8E8E93', mb: 1 }}>
                        العميل: {sale.customer_name}
                        {sale.customer_phone && ` - ${sale.customer_phone}`}
                      </Typography>
                    )}
                    
                    {sale.items && sale.items.length > 0 && (
                      <Box sx={{ mt: 1.5 }}>
                        <Typography variant="caption" sx={{ fontWeight: 600, color: '#1C1C1E', display: 'block', mb: 1 }}>
                          المنتجات:
                        </Typography>
                        {sale.items.map((item, itemIndex) => (
                          <Box 
                            key={itemIndex}
                            sx={{ 
                              display: 'flex', 
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              py: 0.75,
                              px: 1.5,
                              mb: 0.5,
                              bgcolor: 'white',
                              borderRadius: '8px',
                              border: '1px solid rgba(0,0,0,0.04)'
                            }}
                          >
                            <Box sx={{ flex: 1 }}>
                              <Typography variant="body2" sx={{ fontSize: '0.85rem', fontWeight: 600, mb: 0.25 }}>
                                {item.product_name}
                              </Typography>
                              <Typography variant="caption" sx={{ color: '#8E8E93', fontSize: '0.7rem' }}>
                                {item.product_sku} | الكمية: {item.quantity}
                              </Typography>
                            </Box>
                            <Box sx={{ textAlign: 'left' }}>
                              <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.85rem' }}>
                                {parseFloat(item.unit_price || 0).toFixed(0)} IQD
                              </Typography>
                              {item.purchase_price && (
                                <Typography variant="caption" sx={{ color: '#FF9500', fontSize: '0.7rem' }}>
                                  شراء: {parseFloat(item.purchase_price).toFixed(0)}
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        ))}
                      </Box>
                    )}
                  </Box>
                ))}
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 3, gap: 1.5 }}>
            <Button 
              onClick={() => setSalesHistoryOpen(false)} 
              size="large"
              sx={{
                flex: 1,
                py: 1.25,
                borderRadius: '14px',
                color: '#FF3B30',
                fontWeight: 600,
                fontSize: '1rem'
              }}
            >
              إغلاق
            </Button>
            <Button
              onClick={exportSalesHistoryToExcel}
              variant="contained"
              size="large"
              disabled={salesHistory.length === 0}
              sx={{
                flex: 1.5,
                py: 1.25,
                borderRadius: '14px',
                bgcolor: '#34C759',
                fontWeight: 600,
                fontSize: '1rem',
                boxShadow: '0 4px 16px rgba(52, 199, 89, 0.4)',
                '&:hover': { 
                  bgcolor: '#28A745',
                  boxShadow: '0 6px 20px rgba(52, 199, 89, 0.5)'
                }
              }}
              startIcon={<DownloadIcon />}
            >
              تصدير إكسل
            </Button>
          </DialogActions>
        </Dialog>

        {/* Notifications - iOS Style */}
        <Snackbar 
          open={!!success} 
          autoHideDuration={4000} 
          onClose={() => setSuccess('')}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert 
            onClose={() => setSuccess('')} 
            severity="success" 
            sx={{ 
              fontSize: '1rem',
              borderRadius: '14px',
              boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
              bgcolor: '#34C759',
              color: 'white',
              '& .MuiAlert-icon': {
                color: 'white'
              }
            }}
          >
            {success}
          </Alert>
        </Snackbar>

        <Snackbar 
          open={!!error} 
          autoHideDuration={4000} 
          onClose={() => setError('')}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert 
            onClose={() => setError('')} 
            severity="error" 
            sx={{ 
              fontSize: '1rem',
              borderRadius: '14px',
              boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
              bgcolor: '#FF3B30',
              color: 'white',
              '& .MuiAlert-icon': {
                color: 'white'
              }
            }}
          >
            {error}
          </Alert>
        </Snackbar>
      </Box>
    </ThemeProvider>
  );
};

export default POSSystem;
